import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react';

interface SoundContextState {
  isMuted: boolean;
  toggleMute: () => void;
  playOrderBlip: () => void;
}

const SoundContext = createContext<SoundContextState | undefined>(undefined);

export const SoundProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isMuted, setIsMuted] = useState(
    () => localStorage.getItem('orderSoundMuted') === 'true'
  );
  const isMutedRef = useRef(isMuted);
  const audioCtxRef = useRef<AudioContext | null>(null);

  useEffect(() => {
    isMutedRef.current = isMuted;
  }, [isMuted]);

  const toggleMute = useCallback(() => {
    setIsMuted(prev => {
      const next = !prev;
      isMutedRef.current = next;
      localStorage.setItem('orderSoundMuted', String(next));
      return next;
    });
  }, []);

  const playOrderBlip = useCallback(() => {
    if (isMutedRef.current) return;
    try {
      const Ctor =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext })
          .webkitAudioContext;
      if (!audioCtxRef.current) {
        audioCtxRef.current = new Ctor();
      }
      const ctx = audioCtxRef.current;
      if (ctx.state === 'suspended') {
        void ctx.resume();
      }
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.setValueAtTime(600, ctx.currentTime);
      osc.frequency.linearRampToValueAtTime(900, ctx.currentTime + 0.08);
      gain.gain.setValueAtTime(0.15, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.2);
    } catch {
      /* AudioContext not available */
    }
  }, []);

  return (
    <SoundContext.Provider value={{ isMuted, toggleMute, playOrderBlip }}>
      {children}
    </SoundContext.Provider>
  );
};

export const useSound = (): SoundContextState => {
  const context = useContext(SoundContext);
  if (context === undefined)
    throw new Error('useSound must be used within a SoundProvider');
  return context;
};
